import React, { useCallback } from 'react';
import { Popover } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { getProjectState, useViewport, viewportActions } from '@starflow/app/store';
import { eventBus } from '@starflow/app/helpers';
import { calcMultiElementsRect } from '../../../helpers/element';

export const ZoomControls = React.memo(() => {
  const viewport = useViewport();
  const [scaleOptionsVisible, { open: showScaleOptions, close: hideScaleOptions }] = useDisclosure();

  const renderedScale = (viewport.scale * 100).toFixed(0);

  const hanldeRequestOpenHotkeys = useCallback(() => {
    if (!localStorage.getItem('hotkeys_shown')) {
      eventBus.emit('event_request_open_hotkeys');
      localStorage.setItem('hotkeys_shown', '1');
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    hanldeRequestOpenHotkeys();
    viewportActions.zoomOut('screen-center');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoomIn = useCallback(() => {
    hanldeRequestOpenHotkeys();
    viewportActions.zoomIn('screen-center');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlelocate = useCallback(() => {
    const elements = getProjectState('elements');
    const selection = getProjectState('selection');

    if (!selection.length) {
      try {
        const lastGeneratorRect = localStorage.getItem('lastGeneratorRect');

        if (lastGeneratorRect) {
          viewportActions.locateRect(JSON.parse(lastGeneratorRect), {
            paddings: [100, 100, 200, 100],
            animate: true,
            fitView: true,
          });
        } else {
          viewportActions.fitView();
        }
      } catch {
        viewportActions.fitView();
      }
    } else {
      const selectedElements = elements.filter((element) => selection.includes(element.id));
      viewportActions.locateRect(calcMultiElementsRect(selectedElements), {
        paddings: [100, 100, 200, 100],
        animate: true,
        fitView: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.scale]);

  useHotkeys(
    [
      ['mod + =', viewportActions.zoomIn],
      ['mod + -', viewportActions.zoomOut],
      ['mod + 0', viewportActions.zoomReset],
      ['mod + l', handlelocate],
    ],
    undefined,
    true,
  );

  return (
    <div className="flex items-center">
      <button onClick={handleZoomOut} className="flex items-center justify-center w-5 h-5 hover:bg-[#f2f4f6]">
        <Icon className="shk-zoomout" fontSize={18} color="#7f7f7f" />
      </button>
      <Popover opened={scaleOptionsVisible} position="bottom-center">
        <Popover.Target>
          <span
            onMouseEnter={showScaleOptions}
            onMouseLeave={hideScaleOptions}
            className="w-[34px] mx-1 text-center text-xs text-black cursor-pointer"
          >
            {renderedScale}%
          </span>
        </Popover.Target>
        <Popover.Dropdown
          w={180}
          onMouseEnter={showScaleOptions}
          onMouseLeave={hideScaleOptions}
          className="w-[180px] -mt-2 ml-4 p-0 pt-4 bg-transparent border-none"
        >
          <div className="flex flex-col items-stretch divide-y divide-[#f2f4f6] bg-white rounded-sm shadow-light">
            <button
              className="h-8 px-4 hover:bg-[#f2f4f6] font-semibold text-left text-xs text-[#2d2d2d]"
              onClick={() => viewportActions.zoomTo(0.1, 'screen-center')}
            >
              10%
            </button>
            <button
              className="h-8 px-4 hover:bg-[#f2f4f6] font-semibold text-left text-xs text-[#2d2d2d]"
              onClick={() => viewportActions.zoomTo(0.25, 'screen-center')}
            >
              25%
            </button>
            <button
              className="h-8 px-4 hover:bg-[#f2f4f6] font-semibold text-left text-xs text-[#2d2d2d]"
              onClick={() => viewportActions.zoomTo(0.5, 'screen-center')}
            >
              50%
            </button>
            <button
              className="h-8 px-4 hover:bg-[#f2f4f6] font-semibold text-left text-xs text-[#2d2d2d]"
              onClick={() => viewportActions.zoomReset('screen-center')}
            >
              100%
            </button>
            <button
              className="h-8 px-4 hover:bg-[#f2f4f6] font-semibold text-left text-xs text-[#2d2d2d]"
              onClick={() => viewportActions.zoomTo(2, 'screen-center')}
            >
              200%
            </button>
            <button
              className="h-8 px-4 hover:bg-[#f2f4f6] font-semibold text-left text-xs text-[#2d2d2d]"
              onClick={() => viewportActions.fitView()}
            >
              {translate('common_zoom_fit')}
            </button>
          </div>
        </Popover.Dropdown>
      </Popover>
      <button onClick={handleZoomIn} className="flex items-center justify-center w-5 h-5 hover:bg-[#f2f4f6]">
        <Icon className="shk-zoomin" fontSize={18} color="#7f7f7f" />
      </button>
      <button className="flex items-center justify-center w-5 h-5 ml-3 hover:bg-[#f2f4f6]" onClick={handlelocate}>
        <Icon className="shk-position" fontSize={18} color="#7f7f7f" />
      </button>
    </div>
  );
});
